import { AnimatePresence, motion } from 'motion/react';
import { X, History } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import {
  User,
  db,
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  onSnapshot,
} from '../../firebase';

interface AppPayment {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
}

interface BillingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

export function BillingsModal({ isOpen, onClose, user }: BillingsModalProps) {
  const [paymentHistory, setPaymentHistory] = useState<AppPayment[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchPaymentHistory();
      const unsub = onSnapshot(
        doc(db, 'users', user.uid),
        (doc) => {
          if (doc.exists()) {
            setSubscriptionStatus(doc.data()?.subscriptionStatus || null);
          }
        },
        (error) => {
          console.error('User document listener failed:', error);
        }
      );
      return () => unsub();
    }
  }, [isOpen, user]);

  const fetchPaymentHistory = async () => {
    if (!user) return;
    setIsLoadingHistory(true);
    try {
      const q = query(
        collection(db, 'users', user.uid, 'app_payments'),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as AppPayment
      );
      setPaymentHistory(history);
    } catch (error) {
      console.error('Error fetching payment history', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!user) return;
    if (confirm('Do you want to reactivate your subscription?')) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          subscriptionStatus: 'active',
          autoRenew: true,
        });
      } catch (error) {
        console.error('Error reactivating subscription', error);
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    if (
      confirm(
        'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.'
      )
    ) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          subscriptionStatus: 'canceled',
          autoRenew: false,
        });
      } catch (error) {
        console.error('Error cancelling subscription', error);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-[32px] border border-zinc-100 bg-white shadow-2xl transition-colors dark:border-zinc-900 dark:bg-black"
          >
            <div className="space-y-6 overflow-y-auto p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                    Billings
                  </h2>
                  <p className="text-xs font-medium text-zinc-500">
                    Manage your subscription and billing history
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400 transition-colors hover:text-zinc-900 dark:bg-zinc-900 dark:hover:text-zinc-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Subscription Status */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      Current Plan
                    </label>
                    <span
                      className={cn(
                        'rounded-lg px-2 py-1 text-[10px] font-bold tracking-wider uppercase',
                        subscriptionStatus === 'active'
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-zinc-100 text-zinc-500'
                      )}
                    >
                      {subscriptionStatus || 'Free'}
                    </span>
                  </div>

                  {subscriptionStatus === 'active' && (
                    <button
                      onClick={handleCancelSubscription}
                      className="w-full rounded-2xl bg-rose-50 py-3 text-xs font-bold text-rose-600 transition-colors hover:bg-rose-100 dark:bg-rose-900/10 dark:text-rose-400 dark:hover:bg-rose-900/20"
                    >
                      Cancel Subscription
                    </button>
                  )}

                  {subscriptionStatus === 'canceled' && (
                    <button
                      onClick={handleReactivateSubscription}
                      className="w-full rounded-2xl bg-indigo-50 py-3 text-xs font-bold text-indigo-600 transition-colors hover:bg-indigo-100 dark:bg-indigo-900/10 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                    >
                      Reactivate Subscription
                    </button>
                  )}
                </div>

                <div className="h-px bg-zinc-100 dark:bg-zinc-800" />

                {/* Payment History */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                    <History className="h-4 w-4" />
                    <h3>Payment History</h3>
                  </div>
                  <div className="max-h-60 space-y-3 overflow-y-auto rounded-2xl bg-zinc-50 p-4 dark:bg-zinc-900">
                    {isLoadingHistory ? (
                      <div className="py-2 text-center text-xs text-zinc-400">
                        Loading history...
                      </div>
                    ) : paymentHistory.length === 0 ? (
                      <div className="py-2 text-center text-xs text-zinc-400">
                        No payment history found.
                      </div>
                    ) : (
                      paymentHistory.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center justify-between text-xs"
                        >
                          <span className="text-zinc-500 dark:text-zinc-400">
                            {new Date(payment.date).toLocaleDateString()}
                          </span>
                          <span className="font-bold text-zinc-900 dark:text-zinc-100">
                            {payment.currency} {payment.amount}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
